/**
 * @fileoverview CSI Specification Constants
 * All tunable parameters for the CAP Stress Index computation.
 * @module csi/spec
 */

/**
 * @typedef {import('./types.js').CSIPillar} CSIPillar
 * @typedef {import('./types.js').CSIRegime} CSIRegime
 */

// ============================================================================
// CSI SPECIFICATION v1.2
// ============================================================================

/**
 * Complete CSI specification.
 * Frozen to prevent accidental mutation.
 * @type {Readonly<Object>}
 */
export const CSI_SPEC = Object.freeze({
  
  // --------------------------------------------------------------------------
  // VERSION
  // --------------------------------------------------------------------------
  
  VERSION: '1.2.0',
  
  // --------------------------------------------------------------------------
  // SIGNAL ELIGIBILITY
  // --------------------------------------------------------------------------
  
  /**
   * Only signals with these tiers are included in CSI.
   * Source data is excluded — only CAP-derived signals count.
   */
  ELIGIBILITY: Object.freeze({
    /** Tiers eligible for CSI inclusion */
    includedTiers: Object.freeze(['derived', 'composite', 'index', 'proprietary']),
    
    /** Explicit exclusion list (signal IDs) */
    excludedSignals: Object.freeze([
      'SP500', 'DJIA', 'NASDAQ', // Equity levels
      'GDP', 'GDPC1',            // GDP
      'UNRATE',                   // Unemployment
      'CPIAUCSL', 'PCEPI',       // Inflation
    ]),
  }),
  
  // --------------------------------------------------------------------------
  // CONTRIBUTION MAPPING (REDESIGNED)
  // --------------------------------------------------------------------------
  
  /**
   * Percentile-based contribution mapping.
   * Uses historical percentile directly instead of z-score sigmoid.
   * Applies asymmetric power curve to amplify stress extremes.
   */
  CONTRIBUTION: Object.freeze({
    /** Method: 'percentile' (new) or 'zscore' (legacy) */
    method: 'percentile',
    
    /**
     * Power exponent for asymmetric stretching.
     * < 1.0 stretches extremes (amplifies high/low values)
     * = 1.0 linear (no transformation)
     * > 1.0 compresses extremes
     */
    stressPower: 0.75,    // Amplify high stress (upper percentiles)
    calmPower: 1.25,      // Compress low stress (makes calm less sticky)
    
    /** Percentile thresholds for stress amplification */
    stressThreshold: 50,  // Above this percentile, apply stressPower
    
    /** Minimum/maximum contribution bounds */
    minContribution: 2,   // Never fully "calm" — some base stress
    maxContribution: 98,  // Never fully "crisis" — leave room
    
    /** Momentum bonus: extra contribution for trending signals */
    momentumWeight: 0.15, // 15% bonus from momentum
    momentumWindow: 10,   // Days for momentum calculation
  }),
  
  // --------------------------------------------------------------------------
  // LEGACY Z-SCORE (for reference/fallback)
  // --------------------------------------------------------------------------
  
  /**
   * Z-score normalization settings (legacy, used if method='zscore').
   */
  NORMALIZATION: Object.freeze({
    lookbackDays: 1260,
    madScale: 1.4826,
    zClipLower: -3,
    zClipUpper: 4,        // Asymmetric: allow higher stress readings
    minHistoryLength: 20,
  }),
  
  // --------------------------------------------------------------------------
  // AGGREGATION (REDESIGNED)
  // --------------------------------------------------------------------------
  
  /**
   * Aggregation settings — less averaging, more extreme sensitivity.
   */
  AGGREGATION: Object.freeze({
    /** 
     * Family aggregation method:
     * 'max' - Use highest stress signal in family (recommended)
     * 'mean' - Average (dampens extremes)
     * 'weighted_max' - 70% max + 30% mean
     */
    familyMethod: 'weighted_max',
    familyMaxWeight: 0.70,
    
    /**
     * Pillar aggregation method:
     * 'geometric' - Geometric mean (sensitive to extremes)
     * 'arithmetic' - Arithmetic mean (dampens extremes)
     * 'power' - Power mean with exponent
     */
    pillarMethod: 'power',
    pillarPowerExponent: 1.5, // > 1 biases toward higher values
    
    /** Minimum families required for valid pillar */
    minFamilies: 2,
    
    /** EMA span for impulse calculation */
    emaSpan: 20,
  }),
  
  // --------------------------------------------------------------------------
  // OVERALL SCORE CALCULATION
  // --------------------------------------------------------------------------
  
  /**
   * How to combine pillar scores into overall CSI.
   */
  OVERALL: Object.freeze({
    /**
     * Method: 'power' | 'geometric' | 'arithmetic'
     */
    method: 'power',
    powerExponent: 1.3, // Slight bias toward higher pillar scores
    
    /** Floor: minimum CSI even when everything is calm */
    floor: 5,
    
    /** Ceiling: maximum CSI even in extreme crisis */
    ceiling: 98,
  }),
  
  // --------------------------------------------------------------------------
  // PILLAR DEFINITIONS
  // --------------------------------------------------------------------------
  
  /**
   * Pillar weights and metadata.
   * @type {Record<CSIPillar, {weight: number, name: string, description: string}>}
   */
  PILLARS: Object.freeze({
    FUNDING: Object.freeze({
      weight: 1.0,
      name: 'Funding Stress',
      description: 'Short-term funding market conditions',
    }),
    LIQUIDITY: Object.freeze({
      weight: 1.0,
      name: 'Liquidity Stress',
      description: 'System-wide dollar liquidity',
    }),
    RATES_CURVE: Object.freeze({
      weight: 1.0,
      name: 'Rates & Curve',
      description: 'Yield curve shape and term structure stress',
    }),
    CREDIT: Object.freeze({
      weight: 1.0,
      name: 'Credit Stress',
      description: 'Credit spreads and default risk',
    }),
    VOL_EQUITY: Object.freeze({
      weight: 1.0,
      name: 'Equity Volatility',
      description: 'Equity market volatility and fear',
    }),
    VOL_RATES: Object.freeze({
      weight: 1.0,
      name: 'Rates Volatility',
      description: 'Interest rate volatility',
    }),
  }),
  
  // --------------------------------------------------------------------------
  // PILLAR ORDER
  // --------------------------------------------------------------------------
  
  PILLAR_ORDER: Object.freeze([
    'FUNDING',
    'LIQUIDITY',
    'RATES_CURVE',
    'CREDIT',
    'VOL_EQUITY',
    'VOL_RATES',
  ]),
  
  // --------------------------------------------------------------------------
  // REGIME CLASSIFICATION
  // --------------------------------------------------------------------------
  
  REGIMES: Object.freeze({
    lookbackDays: 2520,
    minHistoryForPercentile: 252,
    tradingDaysPerYear: 252,
    
    /**
     * Percentile thresholds — adjusted to spread regimes better.
     */
    thresholds: Object.freeze({
      CALM: Object.freeze({ min: 0, max: 25 }),      // Bottom quartile
      NORMAL: Object.freeze({ min: 25, max: 55 }),   // Middle
      ELEVATED: Object.freeze({ min: 55, max: 75 }), // Upper-middle
      HIGH: Object.freeze({ min: 75, max: 90 }),     // High
      CRISIS: Object.freeze({ min: 90, max: 100 }),  // Top decile
    }),
    
    /**
     * Absolute fallback thresholds — also adjusted.
     */
    absoluteThresholds: Object.freeze({
      CALM: Object.freeze({ min: 0, max: 30 }),
      NORMAL: Object.freeze({ min: 30, max: 50 }),
      ELEVATED: Object.freeze({ min: 50, max: 65 }),
      HIGH: Object.freeze({ min: 65, max: 80 }),
      CRISIS: Object.freeze({ min: 80, max: 100 }),
    }),
    
    display: Object.freeze({
      CALM: Object.freeze({
        label: 'Calm',
        color: 'var(--tg-fog-green)',
        bgColor: 'rgba(151, 184, 160, 0.15)',
      }),
      NORMAL: Object.freeze({
        label: 'Normal',
        color: 'var(--tg-fog-green)',
        bgColor: 'rgba(151, 184, 160, 0.15)',
      }),
      ELEVATED: Object.freeze({
        label: 'Elevated',
        color: 'var(--tg-lantern-gold)',
        bgColor: 'rgba(212, 168, 95, 0.15)',
      }),
      HIGH: Object.freeze({
        label: 'High',
        color: 'var(--tg-rust-red)',
        bgColor: 'rgba(178, 51, 42, 0.15)',
      }),
      CRISIS: Object.freeze({
        label: 'Crisis',
        color: 'var(--tg-flash-crash)',
        bgColor: 'rgba(255, 0, 51, 0.15)',
      }),
    }),
  }),
  
  // --------------------------------------------------------------------------
  // DATA FRESHNESS
  // --------------------------------------------------------------------------
  
  FRESHNESS: Object.freeze({
    defaultSLAHours: 36,
    weeklySLAHours: 72,
    staleBehavior: 'drop_from_pillar',
    minFreshFraction: 0.60,
  }),
  
  // --------------------------------------------------------------------------
  // EXPLAINABILITY
  // --------------------------------------------------------------------------
  
  EXPLAINABILITY: Object.freeze({
    topDriversCount: 8,
    driverScoreWeights: Object.freeze({
      level: 0.60,      // Current contribution level
      change: 0.25,     // Recent change magnitude
      momentum: 0.15,   // Trending component
    }),
    changeWindowDays: 20,
  }),
  
  // --------------------------------------------------------------------------
  // DERIVED SIGNALS
  // --------------------------------------------------------------------------
  
  DERIVED: Object.freeze({
    volatilityWindow: 20,
    minDataPoints: 15,
  }),
  
  // --------------------------------------------------------------------------
  // DEFAULTS
  // --------------------------------------------------------------------------
  
  DEFAULTS: Object.freeze({
    neutralScore: 50,
    neutralContribution: 50,
    defaultConfidence: 0,
    defaultImpulse: 0,
    defaultAcceleration: 0,
  }),
  
});

// ============================================================================
// CONVENIENCE ACCESSORS
// ============================================================================

/**
 * Gets pillar metadata by ID.
 * @param {CSIPillar} pillar
 * @returns {{weight: number, name: string, description: string}|undefined}
 */
export function getPillarMeta(pillar) {
  return CSI_SPEC.PILLARS[pillar];
}

/**
 * Gets regime display properties.
 * @param {CSIRegime} regime
 * @returns {{label: string, color: string, bgColor: string}|undefined}
 */
export function getRegimeDisplay(regime) {
  return CSI_SPEC.REGIMES.display[regime];
}

/**
 * Gets all pillar IDs.
 * @returns {CSIPillar[]}
 */
export function getAllPillars() {
  return /** @type {CSIPillar[]} */ (Object.keys(CSI_SPEC.PILLARS));
}

/**
 * Gets total weight sum.
 * @returns {number}
 */
export function getTotalPillarWeight() {
  return Object.values(CSI_SPEC.PILLARS).reduce((sum, p) => sum + p.weight, 0);
}

/**
 * Gets the neutral score value.
 * @returns {number}
 */
export function getNeutralScore() {
  return CSI_SPEC.DEFAULTS.neutralScore;
}

/**
 * Checks if a tier is eligible for CSI.
 * @param {string} tier
 * @returns {boolean}
 */
export function isEligibleTier(tier) {
  return CSI_SPEC.ELIGIBILITY.includedTiers.includes(tier);
}

/**
 * Checks if a signal is excluded.
 * @param {string} signalId
 * @returns {boolean}
 */
export function isExcludedSignal(signalId) {
  return CSI_SPEC.ELIGIBILITY.excludedSignals.includes(signalId);
}

/** @type {CSIPillar[]} */
export const PILLAR_ORDER = CSI_SPEC.PILLAR_ORDER;
